import { config } from "@/shared/lib/config";
import { addMinutesToTimeHhMm } from "@/shared/lib/date-time";
import {
  formatSlotHours,
  formatSlotHoursFromMinutes,
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  resolveBulkSessionSlotDurationMinutes,
  sumBulkSessionSlotDurationMinutes,
  sumMinutesToSlotHours,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import type { SellPackageFormValues } from "@/shared/ui/SellPackageForm";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  cn,
  FormInput,
  Label,
  RHF,
} from "@devhop/ui";
import { format } from "date-fns";
import {
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { CheckAvailabilityResponseDTO } from "../../domain/contracts/bulk-schedule";
import {
  OFF_SITE_DISTANCE_SELECT_OPTIONS,
  type OffSiteDistance,
} from "../../domain/lib/off-site-distance";
import { isStudioPrivateLessonTemplate } from "../lib/sell-pl-schedule-studio-defaults";
import { useCheckBookingAvailability } from "../api/queries";

type SlotDurationMinutes = PrivateLessonSlotDurationMinutes;
const SLOT_DURATION_SET = PRIVATE_LESSON_SLOT_DURATION_SET;
const DURATION_SELECT_OPTIONS = PRIVATE_LESSON_DURATION_SELECT_OPTIONS;
type LookupItem = { id: string; name: string };

type Props = {
  // zodResolver ອາດໃຫ້ TTransformedValues ຕ່າງຈາກ infer — ປ່ອຍໃຫ້ກົງກັບ FormRoot
  methods: UseFormReturn<SellPackageFormValues, unknown, unknown>;
  purchasedHours: number;
  templateDefaultLocationKind?: "studio" | "off_site";
  onScheduleBlocksSubmit: (blocked: boolean) => void;
};

export function SellPrivateLessonScheduleOnSellSection({
  methods,
  purchasedHours,
  templateDefaultLocationKind = "studio",
  onScheduleBlocksSubmit,
}: Props) {
  const isOffSiteTemplate = templateDefaultLocationKind === "off_site";
  const allowPerSessionLocationKind = isStudioPrivateLessonTemplate({
    defaultLocationKind: templateDefaultLocationKind,
  });
  const plScheduleWithSale = methods.watch("plScheduleWithSale");
  const sessions = methods.watch("plSessions") ?? [];
  const instructorIds = methods.watch("plInstructorIds") ?? [];

  const checkAvailability = useCheckBookingAvailability();
  const [availabilityResult, setAvailabilityResult] =
    useState<CheckAvailabilityResponseDTO | null>(null);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);
  const [internalOverlaps, setInternalOverlaps] = useState<
    Array<{ sessionIndex: number; message: string }>
  >([]);

  const invalidateAvailabilityCheck = useCallback(() => {
    setHasCheckedAvailability(false);
    setAvailabilityResult(null);
    setInternalOverlaps((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const resolveSlotInstructors = (
    slotInstructors: string[] | undefined,
    defaultIds: string[],
  ): string[] =>
    slotInstructors && slotInstructors.length > 0
      ? slotInstructors
      : defaultIds;

  const checkInternalOverlaps = (
    sessionsToCheck: Array<{
      date: string;
      startTime: string;
      endTime: string;
      roomId?: string;
      locationKind?: "studio" | "off_site";
      instructorIds?: string[];
    }>,
    defaultInstructorIds: string[],
  ): Array<{ sessionIndex: number; message: string }> => {
    const overlaps: Array<{ sessionIndex: number; message: string }> = [];

    for (let i = 0; i < sessionsToCheck.length; i++) {
      const session1 = sessionsToCheck[i];
      if (!session1?.date || !session1.startTime || !session1.endTime) {
        continue;
      }

      for (let j = i + 1; j < sessionsToCheck.length; j++) {
        const session2 = sessionsToCheck[j];
        if (!session2?.date || !session2.startTime || !session2.endTime) {
          continue;
        }

        if (session1.date !== session2.date) continue;

        const start1 = new Date(`2000-01-01T${session1.startTime}`);
        const end1 = new Date(`2000-01-01T${session1.endTime}`);
        const start2 = new Date(`2000-01-01T${session2.startTime}`);
        const end2 = new Date(`2000-01-01T${session2.endTime}`);

        if (!(start1 < end2 && end1 > start2)) continue;

        const loc1 = session1.locationKind ?? "studio";
        const loc2 = session2.locationKind ?? "studio";
        const r1 = (session1.roomId ?? "").trim();
        const r2 = (session2.roomId ?? "").trim();
        const sameRoom =
          loc1 === "studio" && loc2 === "studio" && r1.length > 0 && r1 === r2;
        if (sameRoom) {
          const message = "ຫ້ອງທັບຊ້ອນກັບລາຍການອື່ນ";
          if (!overlaps.find((o) => o.sessionIndex === i)) {
            overlaps.push({ sessionIndex: i, message });
          }
          if (!overlaps.find((o) => o.sessionIndex === j)) {
            overlaps.push({ sessionIndex: j, message });
          }
          continue;
        }

        const i1 = resolveSlotInstructors(
          session1.instructorIds,
          defaultInstructorIds,
        );
        const i2 = resolveSlotInstructors(
          session2.instructorIds,
          defaultInstructorIds,
        );
        const shared = i1.filter((id) => i2.includes(id));
        if (shared.length > 0) {
          const message = "ຄູຜູ້ສອນທັບຊ້ອນກັບລາຍການອື່ນ";
          if (!overlaps.find((o) => o.sessionIndex === i)) {
            overlaps.push({ sessionIndex: i, message });
          }
          if (!overlaps.find((o) => o.sessionIndex === j)) {
            overlaps.push({ sessionIndex: j, message });
          }
        }
      }
    }

    return overlaps;
  };

  const totalMinutesToSchedule = useMemo(
    () => sumBulkSessionSlotDurationMinutes(sessions),
    [sessions],
  );

  const totalHoursToSchedule = useMemo(
    () => sumMinutesToSlotHours(totalMinutesToSchedule),
    [totalMinutesToSchedule],
  );

  const hoursBudgetExceeded = totalHoursToSchedule > purchasedHours;

  const prevBlockedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (allowPerSessionLocationKind) return;
    const plSessions = methods.getValues("plSessions") ?? [];
    if (sessions.length === 0) return;
    const loc = templateDefaultLocationKind;
    methods.setValue(
      "plSessions",
      plSessions.map((session) => ({
        ...session,
        locationKind: loc,
        roomId: loc === "off_site" ? "" : (session.roomId ?? ""),
        offSiteDistance:
          loc === "off_site"
            ? (session.offSiteDistance ?? "near")
            : undefined,
      })),
    );
    if (loc === "off_site") {
      methods.setValue("plDefaultRoomId", "");
    }
    invalidateAvailabilityCheck();
  }, [
    templateDefaultLocationKind,
    allowPerSessionLocationKind,
    methods,
    invalidateAvailabilityCheck,
  ]);

  const handleSessionLocationChange = useCallback(
    (index: number, loc: "studio" | "off_site") => {
      methods.setValue(`plSessions.${index}.locationKind`, loc);
      if (loc === "off_site") {
        methods.setValue(`plSessions.${index}.roomId`, "");
        methods.setValue(`plSessions.${index}.offSiteDistance`, "near");
      } else {
        methods.setValue(`plSessions.${index}.offSiteLabel`, "");
        methods.setValue(`plSessions.${index}.offSiteDistance`, undefined);
        const defaultRoom = methods.getValues("plDefaultRoomId")?.trim() ?? "";
        if (defaultRoom) {
          methods.setValue(`plSessions.${index}.roomId`, defaultRoom);
        }
      }
      invalidateAvailabilityCheck();
    },
    [methods, invalidateAvailabilityCheck],
  );

  const addSession = () => {
    const currentSessions = methods.getValues("plSessions") ?? [];
    const defaultRoom = isOffSiteTemplate
      ? ""
      : (methods.getValues("plDefaultRoomId")?.trim() ?? "");
    const loc = templateDefaultLocationKind;
    const start = "09:00";
    const slotDurationMinutes: SlotDurationMinutes = 60;
    methods.setValue("plSessions", [
      ...currentSessions,
      {
        date: format(new Date(), "yyyy-MM-dd"),
        startTime: start,
        slotDurationMinutes,
        endTime: addMinutesToTimeHhMm(start, slotDurationMinutes),
        locationKind: loc,
        offSiteLabel: "",
        offSiteDistance: loc === "off_site" ? ("near" as const) : undefined,
        engagementKind: "standard" as const,
        roomId: loc === "studio" ? defaultRoom : "",
        instructorIds: [] as string[],
        notes: "",
      },
    ]);
    invalidateAvailabilityCheck();
  };

  const removeSession = (index: number) => {
    const currentSessions = methods.getValues("plSessions") ?? [];
    methods.setValue(
      "plSessions",
      currentSessions.filter((_, i) => i !== index),
    );
    invalidateAvailabilityCheck();
  };

  const handleCheckAvailability = async () => {
    if (instructorIds.length === 0 || sessions.length === 0) {
      return;
    }

    const defaultIds = instructorIds as string[];
    const sessionsToCheck = sessions.map((session) => {
      const loc = session.locationKind ?? "studio";
      return {
        date: session.date,
        startTime: session.startTime,
        endTime: session.endTime,
        locationKind: loc,
        engagementKind: session.engagementKind ?? "standard",
        offSiteLabel:
          loc === "off_site" ? session.offSiteLabel?.trim() || null : undefined,
        offSiteDistance:
          loc === "off_site"
            ? (session.offSiteDistance as OffSiteDistance)
            : undefined,
        roomId: loc === "studio" ? session.roomId?.trim() : undefined,
        instructorIds: resolveSlotInstructors(
          session.instructorIds,
          defaultIds,
        ),
      };
    });

    const overlaps = checkInternalOverlaps(sessions, defaultIds);
    setInternalOverlaps(overlaps);

    if (overlaps.length > 0) {
      setHasCheckedAvailability(true);
      setAvailabilityResult({
        available: false,
        conflicts: overlaps.map((overlap) => ({
          sessionIndex: overlap.sessionIndex,
          type: "room" as const,
          message: overlap.message,
        })),
      });
      return;
    }

    try {
      const result = await checkAvailability.mutateAsync({
        sessions: sessionsToCheck,
      });
      setAvailabilityResult(result);
      setHasCheckedAvailability(true);
    } catch {
      // mutation toast
    }
  };

  useEffect(() => {
    const blocked = plScheduleWithSale
      ? instructorIds.length === 0 ||
        sessions.length === 0 ||
        hoursBudgetExceeded ||
        !hasCheckedAvailability ||
        !availabilityResult?.available
      : false;

    if (prevBlockedRef.current === blocked) return;
    prevBlockedRef.current = blocked;
    onScheduleBlocksSubmit(blocked);
  }, [
    plScheduleWithSale,
    instructorIds.length,
    sessions.length,
    hoursBudgetExceeded,
    hasCheckedAvailability,
    availabilityResult?.available,
    onScheduleBlocksSubmit,
  ]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-6">
        <Label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            className="mt-1"
            checked={Boolean(plScheduleWithSale)}
            onCheckedChange={(checked) => {
              const enabled = Boolean(checked);
              methods.setValue("plScheduleWithSale", enabled, {
                shouldValidate: true,
              });
              if (!enabled) {
                invalidateAvailabilityCheck();
                prevBlockedRef.current = false;
                onScheduleBlocksSubmit(false);
              }
            }}
          />
          <div>
            <span className="font-medium text-sm">
              ຈອງຕາຕະລາງຮຽນພ້ອມກັນກັບການຂາຍແພັກເກດ
            </span>
            <p className="mt-1 text-muted-foreground text-xs">
              {isOffSiteTemplate
                ? "ກຳນົດຄູ ແລະ ຄັ້ງຮຽນ (ນອກສະຖານທີ່ — ຈອງເປັນເວລາຄູ) — ກວດສອບຄວາມພ້ອມກ່ອນຢືນຢັນຂາຍ."
                : allowPerSessionLocationKind
                  ? "ກຳນົດຫ້ອງ ຫຼື ສະຖານທີ່ນອກສະຖານທີ່, ຄູ ແລະ ຄັ້ງຮຽນ — ກວດສອບຄວາມພ້ອມກ່ອນຢືນຢັນຂາຍ."
                  : "ກຳນົດຫ້ອງ, ຄູຄ່າເລີ່ມຕົ້ນ ແລະ ຄັ້ງຮຽນ — ກວດສອບຄວາມພ້ອມກ່ອນຢືນຢັນຂາຍ."}
            </p>
          </div>
        </Label>
      </div>

      {plScheduleWithSale && (
        <>
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                3
              </div>
              <h3 className="font-semibold text-lg">
                {isOffSiteTemplate
                  ? "ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"
                  : "ຫ້ອງ ແລະ ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"}
              </h3>
              <span className="ml-auto rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
                ຈຳເປັນ
              </span>
            </div>

            <div
              className={cn(
                "grid gap-4",
                isOffSiteTemplate ? "md:grid-cols-1" : "md:grid-cols-2",
              )}
            >
              {!isOffSiteTemplate && (
                <FormInfiniteCombobox<LookupItem>
                  name="plDefaultRoomId"
                  label="ຫ້ອງຄ່າເລີ່ມຕົ້ນ (ລາຍການໃໝ່)"
                  queryKey={["rooms", "lookup", "sell-pl-default"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite<LookupItem>(
                      `${config.apiUrl}/master/rooms/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<LookupItem>(
                      `${config.apiUrl}/master/rooms/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກຫ້ອງ (ທາງເລືອກ)..."
                />
              )}
              <FormInfiniteMultiCombobox<LookupItem>
                name="plInstructorIds"
                label="ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"
                requiredMark
                onFormValueCommit={invalidateAvailabilityCheck}
                queryKey={["instructors", "lookup", "sell-pl"]}
                queryFn={(args) =>
                  fetchLookupForInfinite<LookupItem>(
                    `${config.apiUrl}/master/instructors/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(ids) =>
                  hydrateLookupItems<LookupItem>(
                    `${config.apiUrl}/master/instructors/lookup`,
                    ids,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກຄູຜູ້ສອນ..."
              />
            </div>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                4
              </div>
              <h3 className="font-semibold text-lg">ກຳນົດຕາຕະລາງຮຽນ</h3>
            </div>

            <div className="space-y-4">
              {sessions.length > 0 && (
                <div className="mx-auto w-full max-w-xl space-y-3">
                  {sessions.map((session, index) => {
                    let hours = "0";
                    const minutes = resolveBulkSessionSlotDurationMinutes(session);
                    if (minutes > 0) {
                      hours = formatSlotHoursFromMinutes(minutes);
                    }

                    const sessionLocationKind =
                      session.locationKind ??
                      templateDefaultLocationKind ??
                      "studio";
                    const showStudioFields =
                      allowPerSessionLocationKind
                        ? sessionLocationKind === "studio"
                        : !isOffSiteTemplate;

                    const sessionConflicts =
                      availabilityResult?.conflicts.filter(
                        (c) => c.sessionIndex === index,
                      ) || [];
                    const internalOverlap = internalOverlaps.find(
                      (o) => o.sessionIndex === index,
                    );
                    const hasConflict =
                      sessionConflicts.length > 0 || !!internalOverlap;
                    const isAvailable = hasCheckedAvailability && !hasConflict;

                    const conflictMessage =
                      internalOverlap?.message ||
                      sessionConflicts[0]?.message ||
                      "";

                    const statusRing = hasCheckedAvailability
                      ? hasConflict
                        ? "border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/25"
                        : "border-green-300 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20"
                      : "border-border bg-card";

                    return (
                      <div
                        key={`sell-pl-slot-${String(index)}`}
                        className={cn(
                          "rounded-lg border p-4 shadow-sm",
                          statusRing,
                        )}
                      >
                        <div className="mb-3 flex flex-wrap items-start justify-between gap-2 border-border/80 border-b pb-3">
                          <div>
                            <p className="font-semibold text-sm">
                              ລາຍການທີ່ {index + 1}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {hours} ຊົ່ວໂມງ
                              {hasCheckedAvailability && (
                                <span className="ml-2 inline-flex items-center gap-1 align-middle">
                                  {isAvailable ? (
                                    <CheckCircleIcon className="size-3.5 text-green-600" />
                                  ) : (
                                    <XCircleIcon className="size-3.5 text-red-600" />
                                  )}
                                </span>
                              )}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={() => removeSession(index)}
                          >
                            <TrashIcon className="mr-1 size-3.5" />
                            ລຶບ
                          </Button>
                        </div>

                        {hasConflict && conflictMessage ? (
                          <p className="mb-3 text-red-600 text-xs leading-snug">
                            {conflictMessage}
                          </p>
                        ) : null}

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5 sm:col-span-2">
                            <Label className="text-muted-foreground text-xs">
                              ວັນທີ
                            </Label>
                            <RHF.Controller
                              name={`plSessions.${index}.date`}
                              control={methods.control}
                              render={({ field }) => (
                                <input
                                  type="date"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    invalidateAvailabilityCheck();
                                  }}
                                  className="h-9 w-full max-w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                />
                              )}
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">
                              ເວລາເລີ່ມຕົ້ນ
                            </Label>
                            <RHF.Controller
                              name={`plSessions.${index}.startTime`}
                              control={methods.control}
                              render={({ field }) => (
                                <QuarterHourMinuteSelect
                                  value={field.value ?? ""}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    const dur = methods.getValues(
                                      `plSessions.${index}.slotDurationMinutes`,
                                    ) as SlotDurationMinutes;
                                    methods.setValue(
                                      `plSessions.${index}.endTime`,
                                      addMinutesToTimeHhMm(v, dur),
                                    );
                                    invalidateAvailabilityCheck();
                                  }}
                                />
                              )}
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">
                              ຄວາມຍາວ (ນາທີ)
                            </Label>
                            <RHF.Controller
                              name={`plSessions.${index}.slotDurationMinutes`}
                              control={methods.control}
                              render={({ field: durField }) => {
                                const raw = Number(durField.value);
                                const safe = SLOT_DURATION_SET.has(raw)
                                  ? raw
                                  : 60;
                                return (
                                  <SimpleSelect
                                    value={String(safe)}
                                    onValueChange={(val) => {
                                      const m = Number(val);
                                      if (!SLOT_DURATION_SET.has(m)) return;
                                      durField.onChange(m);
                                      const st = methods.getValues(
                                        `plSessions.${index}.startTime`,
                                      );
                                      if (st) {
                                        methods.setValue(
                                          `plSessions.${index}.endTime`,
                                          addMinutesToTimeHhMm(st, m),
                                        );
                                      }
                                      invalidateAvailabilityCheck();
                                    }}
                                    options={DURATION_SELECT_OPTIONS}
                                    className="h-9 w-full"
                                    placeholder="ເລືອກຄວາມຍາວ"
                                  />
                                );
                              }}
                            />
                          </div>

                          <div className="space-y-1.5 sm:col-span-2">
                            <Label className="text-muted-foreground text-xs">
                              ເວລາສິ້ນສຸດ
                            </Label>
                            <RHF.Controller
                              name={`plSessions.${index}.endTime`}
                              control={methods.control}
                              render={({ field }) => (
                                <div className="flex h-9 w-full max-w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                                  {field.value || "—"}
                                </div>
                              )}
                            />
                          </div>

                          {allowPerSessionLocationKind ? (
                            <div className="space-y-1.5 sm:col-span-2">
                              <Label className="text-muted-foreground text-xs">
                                ປະເພດສະຖານທີ່
                              </Label>
                              <RHF.Controller
                                name={`plSessions.${index}.locationKind`}
                                control={methods.control}
                                render={({ field }) => (
                                  <SimpleSelect
                                    value={field.value ?? "studio"}
                                    onValueChange={(v) => {
                                      const loc = v as "studio" | "off_site";
                                      field.onChange(loc);
                                      handleSessionLocationChange(index, loc);
                                    }}
                                    options={[
                                      { value: "studio", label: "ສະຕູດິໂອ" },
                                      {
                                        value: "off_site",
                                        label: "ນອກສະຖານທີ່",
                                      },
                                    ]}
                                    className="h-9 w-full"
                                  />
                                )}
                              />
                            </div>
                          ) : null}

                          {showStudioFields ? (
                            <div className="space-y-1.5 sm:col-span-2">
                              <FormInfiniteCombobox<LookupItem>
                                key={`sell-pl-room-${String(index)}`}
                                name={`plSessions.${index}.roomId`}
                                label="ຫ້ອງ"
                                onFormValueCommit={invalidateAvailabilityCheck}
                                queryKey={[
                                  "rooms",
                                  "lookup",
                                  "sell",
                                  String(index),
                                ]}
                                queryFn={(args) =>
                                  fetchLookupForInfinite<LookupItem>(
                                    `${config.apiUrl}/master/rooms/lookup`,
                                    args,
                                  )
                                }
                                preloadQueryFn={(id) =>
                                  hydrateLookupItem<LookupItem>(
                                    `${config.apiUrl}/master/rooms/lookup`,
                                    id,
                                  )
                                }
                                getLabel={(item) => item.name}
                                getValue={(item) => item.id}
                                placeholder="ເລືອກຫ້ອງ..."
                              />
                            </div>
                          ) : (
                            <div className="space-y-3 sm:col-span-2">
                              <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">
                                  ໄລຍະນອກສະຖານທີ່
                                </Label>
                                <RHF.Controller
                                  name={`plSessions.${index}.offSiteDistance`}
                                  control={methods.control}
                                  render={({ field }) => (
                                    <SimpleSelect
                                      value={field.value ?? "near"}
                                      onValueChange={(v) => {
                                        field.onChange(v as OffSiteDistance);
                                        invalidateAvailabilityCheck();
                                      }}
                                      options={[
                                        ...OFF_SITE_DISTANCE_SELECT_OPTIONS,
                                      ]}
                                      className="h-9 w-full"
                                    />
                                  )}
                                />
                              </div>
                              <FormInput
                                name={`plSessions.${index}.offSiteLabel`}
                                label="ຊື່ສະຖານທີ່ (ທາງເລືອກ)"
                                placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ"
                              />
                            </div>
                          )}

                          <div className="space-y-1.5 sm:col-span-2">
                            <FormInfiniteMultiCombobox<LookupItem>
                              name={`plSessions.${index}.instructorIds`}
                              label="ຄູ (ສະເພາະລາຍການ · ວ່າງ = ຄ່າເລີ່ມຕົ້ນ)"
                              onFormValueCommit={invalidateAvailabilityCheck}
                              queryKey={[
                                "instructors",
                                "lookup",
                                "sell-slot",
                                String(index),
                              ]}
                              queryFn={(args) =>
                                fetchLookupForInfinite<LookupItem>(
                                  `${config.apiUrl}/master/instructors/lookup`,
                                  args,
                                )
                              }
                              preloadQueryFn={(ids) =>
                                hydrateLookupItems<LookupItem>(
                                  `${config.apiUrl}/master/instructors/lookup`,
                                  ids,
                                )
                              }
                              getLabel={(item) => item.name}
                              getValue={(item) => item.id}
                              placeholder="ໃຊ້ຄ່າເລີ່ມຕົ້ນ"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={addSession}
                disabled={
                  instructorIds.length === 0 ||
                  totalHoursToSchedule >= purchasedHours
                }
              >
                <PlusIcon className="mr-2 size-4" />
                ເພີ່ມລາຍການອີກ
              </Button>

              {instructorIds.length === 0 && (
                <Alert>
                  <AlertDescription>
                    ກະລຸນາເລືອກຄູຜູ້ສອນກ່ອນເພີ່ມລາຍການ
                  </AlertDescription>
                </Alert>
              )}
              {instructorIds.length > 0 &&
                totalHoursToSchedule >= purchasedHours &&
                sessions.length > 0 && (
                  <Alert>
                    <AlertDescription>
                      ຊົ່ວໂມງຕາມຕາຕະລາງຄົບຕາມຊົ່ວໂມງທີ່ຊື້ແລ້ວ — ລົບ ຫຼື
                      ປັບເວລາກ່ອນເພີ່ມ
                    </AlertDescription>
                  </Alert>
                )}
              {hoursBudgetExceeded && (
                <Alert variant="destructive">
                  <AlertDescription>
                    ຍອດຊົ່ວໂມງທີ່ຈະຫັກ ({formatSlotHours(totalHoursToSchedule)} ຊົ່ວໂມງ) ເກີນ{" "}
                    {purchasedHours} ຊົ່ວໂມງທີ່ຊື້
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {sessions.length > 0 && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">ລາຍການທັງໝົດ:</span>
                  <p className="font-bold">{sessions.length} ຄັ້ງ</p>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    ຊົ່ວໂມງທີ່ຈະຫັກ:
                  </span>
                  <p className="font-bold">
                    {formatSlotHours(totalHoursToSchedule)} ຊົ່ວໂມງ — ຊື້{" "}
                    {purchasedHours} ຊົ່ວໂມງ
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCheckAvailability}
              disabled={
                sessions.length === 0 ||
                instructorIds.length === 0 ||
                hoursBudgetExceeded ||
                checkAvailability.isPending
              }
            >
              {checkAvailability.isPending ? "ກຳລັງກວດສອບ..." : "ກວດສອບຄວາມພ້ອມ"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
